import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Button, Form, Dropdown, message, DatePicker, Image, Select, AutoComplete } from "antd";
import type { MenuProps } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";
import { SHOW_DRAWINGS_SECTION } from "constants/constants";
import { pruneEmptyValues } from "utils/helpers";
import dayjs from "dayjs";

import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";
import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { UserRoleValue, warrantyType, AttachmentType, PropertyDocumentsType } from "constants/field-values";
import {
	BUILDING_TYPE_OPTIONS,
	OCCUPANCY_CLASS_OPTIONS,
	CONSTRUCTION_TYPE_OPTIONS,
	FIRE_SUPPRESSION_OPTIONS,
	NUMBER_OF_STORIES_OPTIONS,
	NUMBER_OF_UNITS_OPTIONS,
	YEAR_BUILT_OPTIONS
} from "constants/property-field-options";
import routeNames from "constants/route-names";
import { compareEntities, getPropertyAddress } from "utils/helpers";

import {
	Property_Documents_Insert_Input,
	Properties_Set_Input,
	Users,
	User_Properties
} from "graphql/generated/graphql";
import { useGetPropertyByIdQuery } from "graphql/queries/get-property-by-id.generated";
import { useGetUsersByOrganizationIdsQuery } from "graphql/queries/get-users-by-organization-ids.generated";
import { useUpdatePropertyByIdMutation } from "graphql/mutations/update-property-by-id.generated";
import { useAddPropertyContactMutation } from "graphql/mutations/add-property-contact.generated";
import { useUpdatePropertyContactByIdMutation } from "graphql/mutations/update-property-contact-by-id.generated";
import { useDeleteUserPropertiesMutation } from "graphql/mutations/delete-user-properties.generated";
import { useAddUserPropertiesMutation } from "graphql/mutations/add-user-properties.generated";
import { useDeletePropertyDocumentByIdMutation } from "graphql/mutations/delete-property-document-by-id.generated";
import { useAddPropertyDocumentsMutation } from "graphql/mutations/add-property-documents.generated";
import { useExtractPropertyFloorPlanMutation } from "graphql/mutations/extract-property-floor-plan.generated";
import { useInsertAddressMutation } from "graphql/mutations/insert-address.generated";
import { useUpdateAddressByIdMutation } from "graphql/mutations/update-address-by-id.generated";
import { useGetAddressByDetailsLazyQuery } from "graphql/queries/get-address-by-details.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { deleteFromStorage, getFileExtension, uploadToStorage } from "utils/files-management";
import { useAuth } from "context/AuthContext";
import { core, usAutocompletePro } from "smartystreets-javascript-sdk";

const smartyClient = new core.ClientBuilder(
	new core.SharedCredentials(process.env.REACT_APP_SMARTY_API_KEY || "")
).buildUsAutocompleteProClient();

const PropertyEdit: React.FC = () => {
	const { updateSelectedAttachments } = useSelectedAttachments();
	const { loggedUserValue } = useLoggedUser();
	const { refreshToken } = useAuth();
	const params = useParams();
	const navigate = useNavigate();
	const [name, setName] = useState<string>("");
	const [address, setAddress] = useState<string>("");
	const [addressOptions, setAddressOptions] = useState<{ value: string }[]>([]);
	const [addressSuggestions, setAddressSuggestions] = useState<
		{ value: string; street_1: string; street_2?: string; city: string; state: string; postal_code: string }[]
	>([]);
	const [existingAddressId, setExistingAddressId] = useState<string | null>(null);
	const [selectedAddressComponents, setSelectedAddressComponents] = useState<{
		street_1: string;
		street_2?: string;
		city: string;
		state: string;
		postal_code: string;
	} | null>(null);
	const [buildingType, setBuildingType] = useState<string>("");
	const [occupancyClass, setOccupancyClass] = useState<string>("");
	const [numberOfStories, setNumberOfStories] = useState<string>("");
	const [numberOfUnits, setNumberOfUnits] = useState<string>("");
	const [constructionType, setConstructionType] = useState<string>("");
	const [fireSuppression, setFireSuppression] = useState<string>("");
	const [yearBuilt, setYearBuilt] = useState<string>("");
	const [propertyData, setPropertyData] = useState<any>(null);
	const [propertyOrganizationId, setPropertyOrganizationId] = useState<string>("");
	const [addPropertyPhotoModalOpen, setAddPropertyPhotoModalOpen] = useState<boolean>(false);
	const [propertyPhotoThumbUrl, setPropertyPhotoThumbUrl] = useState<string>("");
	const [propertyPhotoFile, setPropertyPhotoFile] = useState<RcFile | null>(null);
	const [contactName, setContactName] = useState<string>("");
	const [contactPhoneNumber, setContactPhoneNumber] = useState<string>("");
	const [contactEmail, setContactEmail] = useState<string>("");
	const [addDocumentModalOpen, setAddDocumentModalOpen] = useState<boolean>(false);
	const [addDocumentModalEditMode, setAddDocumentModalEditMode] = useState<boolean>(false);
	const [drawingsModalOpen, setDrawingsModalOpen] = useState<boolean>(false);
	const [existingDrawingsFiles, setExistingDrawingsFiles] = useState<Array<any>>([]);
	const [drawingsFiles, setDrawingsFiles] = useState<Array<any>>([]);
	const [floorPlansModalOpen, setFloorPlansModalOpen] = useState<boolean>(false);
	const [existingFloorPlanFiles, setExistingFloorPlanFiles] = useState<Array<any>>([]);
	const [floorPlanFiles, setFloorPlanFiles] = useState<Array<any>>([]);
	const [existingDocumentsFiles, setExistingDocumentsFiles] = useState<Array<any>>([]);
	const [documentsFiles, setDocumentsFiles] = useState<Array<any>>([]);

	const [warrantyDateStart, setWarrantyDateStart] = useState<dayjs.Dayjs | null>(null);
	const [warrantyDateEnd, setWarrantyDateEnd] = useState<dayjs.Dayjs | null>(null);
	const [warrantyDocMode, setWarrantyDocMode] = useState<boolean>(false);
	const [warrantyDocFile, setWarrantyDocFile] = useState<any>();
	const [existingWarrantyDocFile, setExistingWarrantyDocFile] = useState<any>();

	const [usersDropdownOpen, setUsersDropdownOpen] = useState<boolean>(false);
	const [initialPropertyUsers, setInitialPropertyUsers] = useState<Users[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<Users[]>([]);
	const [selectedUsersKeys, setSelectedUsersKeys] = useState<Array<string>>([]);
	const [isPremium, setIsPremium] = useState<boolean>(false);
	const [expectedDeviceTypes, setExpectedDeviceTypes] = useState<string[]>([]);

	const [myqSetupLoading, setMyqSetupLoading] = useState<boolean>(false);

	const [uploading, setUploading] = useState<boolean>(false);

	useEffect(() => {
		if (!propertyData?.organization_id) return;

		const loggedUserPropertiesOrganizations = loggedUserValue.user_properties.map(up => up.property?.organization_id);

		const enabled = loggedUserPropertiesOrganizations.includes(propertyData?.organization_id);

		if (!enabled) {
			navigate(routeNames.ADMIN_PROPERTIES);
		}
	}, [propertyData, navigate, loggedUserValue]);

	const handleAddressSearch = useCallback(async (searchText: string) => {
		if (!searchText || searchText.length < 3) {
			setAddressOptions([]);
			setAddressSuggestions([]);
			return;
		}
		try {
			const lookup = new usAutocompletePro.Lookup(searchText);
			lookup.maxResults = 10;
			await smartyClient.send(lookup);
			const suggestions = lookup.result.map((s: InstanceType<typeof usAutocompletePro.Suggestion>) => ({
				value: s.secondary
					? `${s.streetLine} ${s.secondary}, ${s.city}, ${s.state} ${s.zipcode}`
					: `${s.streetLine}, ${s.city}, ${s.state} ${s.zipcode}`,
				street_1: s.streetLine,
				street_2: s.secondary || undefined,
				city: s.city,
				state: s.state,
				postal_code: s.zipcode
			}));
			setAddressSuggestions(suggestions);
			setAddressOptions(suggestions.map(s => ({ value: s.value })));
		} catch (error) {
			console.error("Smarty address autocomplete failed", error);
			setAddressOptions([]);
			setAddressSuggestions([]);
		}
	}, []);

	const handleAddressSelect = useCallback((value: string) => {
		setAddressSuggestions(prev => {
			const match = prev.find(s => s.value === value);
			if (match) {
				setSelectedAddressComponents({
					street_1: match.street_1,
					street_2: match.street_2,
					city: match.city,
					state: match.state,
					postal_code: match.postal_code
				});
			}
			return prev;
		});
	}, []);

	const handleOnErrorGetPropertyById = useCallback((error: ApolloError) => {
		console.log("Get Property By Id Error: ", error);
	}, []);

	const handleOnCompleteGetPropertyBy = useCallback(data => {
		console.log("Get Property By Id Complete: ", data);
		const {
			name,
			address,
			address_id,
			standardized_address,
			premium,
			photo_link,
			property_contacts,
			property_documents,
			warranty_start_date,
			warranty_expiration_date,
			property_users,
			organization_id,
			building_type,
			occupancy_class,
			num_stories,
			num_units,
			construction_type,
			sprinklered,
			year_built,
			expected_device_type
		} = data.properties_by_pk[0] || {};

		setName(name || "");
		setAddress(address || "");
		setExistingAddressId(address_id || null);
		setAddress(getPropertyAddress(address, standardized_address));

		setIsPremium(!!premium);
		setPropertyPhotoThumbUrl(photo_link);
		setPropertyOrganizationId(organization_id || "");
		setBuildingType(building_type || "");
		setOccupancyClass(occupancy_class || "");
		setNumberOfStories(num_stories ? String(num_stories) : "");
		setNumberOfUnits(num_units ? String(num_units) : "");
		setConstructionType(construction_type || "");
		setFireSuppression(sprinklered || "");
		setYearBuilt(year_built ? String(year_built) : "");

		if (expected_device_type && expected_device_type.length) {
			setExpectedDeviceTypes(expected_device_type);
		}

		if (property_contacts && property_contacts.length) {
			const { name: contact_name, number, email } = property_contacts[0];
			setContactName(contact_name || "");
			setContactPhoneNumber(number || "");
			setContactEmail(email || "");
		}

		const drawingDocs = property_documents.filter((el: any) => el.type === PropertyDocumentsType.DRAWING);
		setExistingDrawingsFiles(drawingDocs.length ? drawingDocs : []);

		const floorPlanDocs = property_documents.filter((el: any) => el.type === PropertyDocumentsType.FLOOR_PLAN);
		setExistingFloorPlanFiles(floorPlanDocs.length ? floorPlanDocs : []);

		const propertyDocuments = property_documents.filter(
			(element: any) =>
				element.type !== warrantyType &&
				element.type !== PropertyDocumentsType.DRAWING &&
				element.type !== PropertyDocumentsType.FLOOR_PLAN
		);
		if (propertyDocuments.length) {
			setExistingDocumentsFiles(propertyDocuments);
		} else {
			setExistingDocumentsFiles([]);
		}

		const warrantyDocument = property_documents.find(element => element.type === warrantyType);
		if (warrantyDocument) {
			setExistingWarrantyDocFile(warrantyDocument);
		} else {
			setExistingWarrantyDocFile(null);
		}

		if (warranty_start_date) {
			const existingWarrantyDateStart = dayjs(warranty_start_date);
			setWarrantyDateStart(existingWarrantyDateStart);
		}

		if (warranty_expiration_date) {
			const existingWarrantyDateEnd = dayjs(warranty_expiration_date);
			setWarrantyDateEnd(existingWarrantyDateEnd);
		}

		if (property_users.length) {
			const existingUsers: Users[] = [];
			const existingUsersKeys: string[] = [];

			property_users.forEach((element: User_Properties) => {
				if (element.user) {
					existingUsers.push(element.user);
					existingUsersKeys.push(element.user.id);
				}
			});

			console.log("handleOnCompleteGetPropertyBy existingUsers: ", existingUsers);
			setInitialPropertyUsers(existingUsers);
			setSelectedUsers(existingUsers);
			setSelectedUsersKeys(existingUsersKeys);
		}

		setPropertyData(data.properties_by_pk[0]);
	}, []);

	const { loading: loadingPropertyById } = useGetPropertyByIdQuery({
		skip: !params.propertyId,
		variables: {
			id: params.propertyId,
			is_not_deleted: true
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetPropertyById,
		onCompleted: handleOnCompleteGetPropertyBy
	});

	const handleOnRemoveExistingAttachment = useCallback(async (documentFile: any) => {
		const { id, photo_link, document_link, video_link, audio_link, site_link } = documentFile;

		try {
			const docLink = photo_link || document_link || video_link || audio_link || site_link;

			await deleteFromStorage(docLink);
		} catch (error) {
			console.error("Error deleting Property documentFile from firebase:", error);
		}

		try {
			if (id) {
				deletePropertyDocumentByIdMutation({
					variables: {
						id: id
					}
				});
			}
		} catch (error) {
			console.error("Error deleting Property documentFile from database:", error);
		}
	}, []);

	const handleOnRemoveDocument = useCallback(
		(documentFile: any) => {
			const { uid, type, description, file } = documentFile;

			const newList = documentsFiles.filter(element => element.uid !== uid);

			setDocumentsFiles(newList);
		},
		[documentsFiles]
	);

	const handleOnRemoveDrawing = useCallback(
		(documentFile: any) => {
			const { uid } = documentFile;
			setDrawingsFiles(prev => prev.filter(element => element.uid !== uid));
		},
		[drawingsFiles]
	);

	const updateDrawings = useCallback((documentFile: any) => {
		const { uid } = documentFile;
		setDrawingsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);
			return filteredList.concat([documentFile]);
		});
	}, []);

	const updateFloorPlans = useCallback((documentFile: any) => {
		const { uid } = documentFile;
		setFloorPlanFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);
			return filteredList.concat([{ ...documentFile, type: PropertyDocumentsType.FLOOR_PLAN }]);
		});
	}, []);

	const handleOnRemoveFloorPlan = useCallback((documentFile: any) => {
		setFloorPlanFiles(prev => prev.filter(element => element.uid !== documentFile.uid));
	}, []);

	const updateAttachments = useCallback((documentFile: any, warrantyMode?: boolean) => {
		const { type, uid } = documentFile;

		if (warrantyMode) {
			setWarrantyDocFile(documentFile);
			return;
		}

		setDocumentsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);

			return filteredList.concat([documentFile]);
		});
	}, []);

	const handleOnEditDocument = useCallback(
		(documentFile: any) => {
			const { uid, type, description, file, name } = documentFile;

			updateSelectedAttachments(documentFile);

			setAddDocumentModalEditMode(true);
			setAddDocumentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const handleOnCloseDocumentModal = useCallback(async () => {
		setAddDocumentModalOpen(false);
		setAddDocumentModalEditMode(false);
		setWarrantyDocMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setPropertyPhotoThumbUrl(url);
		setPropertyPhotoFile(file);
	}, []);

	const handleOnCompleteUsersByOrganization = useCallback(data => {
		const { users } = data;
		// console.log("Complete Users data: ", users);
	}, []);

	const handleOnErrorUsersByOrganization = useCallback((error: ApolloError) => {
		console.log("handleOnErrorUsers Error: ", error);
	}, []);

	const { data: usersByOrganizationData, loading: loadingUsersByOrganization } = useGetUsersByOrganizationIdsQuery({
		variables: {
			organization_ids: [propertyOrganizationId, loggedUserValue.organization_id].filter(Boolean)
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUsersByOrganization,
		onCompleted: handleOnCompleteUsersByOrganization
	});

	const handleOnSelectUsers = useCallback(
		args => {
			const { selectedKeys } = args;
			const { users = [] } = usersByOrganizationData || {};

			const usersList: Array<Users> = (users as Users[])?.concat(initialPropertyUsers);

			const newSelectedUsers = usersList.reduce((accumulator: Users[], currentValue: Users) => {
				const { id } = currentValue;

				const alreadySaved = accumulator.find(element => element.id === id);

				if (!alreadySaved) {
					selectedKeys.includes(currentValue.id);

					if (selectedKeys.includes(currentValue.id)) {
						accumulator.push(currentValue);
					}
				}

				return accumulator;
			}, []);

			setSelectedUsers(newSelectedUsers);
			setSelectedUsersKeys(selectedKeys);
		},
		[usersByOrganizationData, initialPropertyUsers, selectedUsersKeys]
	);

	const handleOnRemoveUser = useCallback(
		userId => {
			const newSelectedUsers = selectedUsers?.filter(element => element.id !== userId);

			const newSelectedUsersKeys = selectedUsersKeys?.filter(element => element !== userId);

			setSelectedUsers(newSelectedUsers as Array<never>);
			setSelectedUsersKeys(newSelectedUsersKeys);
		},
		[selectedUsers, selectedUsersKeys]
	);

	const handleOnCompleteUpdateProperty = useCallback((data: any) => {
		// console.log("Complete Update Property data: ", data);
	}, []);

	const handleOnErrorUpdateProperty = useCallback((error: ApolloError) => {
		console.log("Update Property Error: ", error);
	}, []);

	const [updatePropertyByIdMutation, { loading: updatePropertyByIdLoading }] = useUpdatePropertyByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdateProperty,
		onCompleted: handleOnCompleteUpdateProperty
	});

	const [insertAddressMutation] = useInsertAddressMutation({ errorPolicy: "none" });
	const [updateAddressByIdMutation] = useUpdateAddressByIdMutation({ errorPolicy: "none" });
	const [getAddressByDetails] = useGetAddressByDetailsLazyQuery({ errorPolicy: "none" });

	const handleOnCompleteAddPropertyContact = useCallback((data: any) => {
		//    console.log("Complete Add  Property Contact data: ", data);
	}, []);

	const handleOnErrorAddPropertyContact = useCallback((error: ApolloError) => {
		// console.log("Add Property Contact Error: ", error);
	}, []);

	const [addPropertyContactMutation, { loading: addPropertyContactLoading }] = useAddPropertyContactMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddPropertyContact,
		onCompleted: handleOnCompleteAddPropertyContact
	});

	const handleOnCompleteUpdatePropertyContact = useCallback((data: any) => {
		// console.log("Complete Update Property Contact data: ", data);
	}, []);

	const handleOnErrorUpdatePropertyContact = useCallback((error: ApolloError) => {
		console.log("Update Property Contact Error: ", error);
	}, []);

	const [updatePropertyContactByIdMutation, { loading: updatePropertyContactByIdLoading }] =
		useUpdatePropertyContactByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorUpdatePropertyContact,
			onCompleted: handleOnCompleteUpdatePropertyContact
		});

	const handleOnCompleteAddUserProperties = useCallback(
		async (data: any) => {
			// console.log("Complete Add User Properties data: ", data);

			const isLoggedUser = data?.insert_user_properties?.returning[0]?.user_id === loggedUserValue.id;

			if (refreshToken && isLoggedUser) {
				await refreshToken();
			}
		},
		[loggedUserValue.id, refreshToken]
	);

	const handleOnErrorAddUserProperties = useCallback((error: ApolloError) => {
		console.log("Add User Properties Error: ", error);
	}, []);

	const [addUserPropertiesMutation, { loading: addUserPropertiesLoading }] = useAddUserPropertiesMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddUserProperties,
		onCompleted: handleOnCompleteAddUserProperties
	});

	const handleOnCompleteDeleteUserProperties = useCallback(
		async (data: any) => {
			// console.log("Complete Delete User Properties data: ", data);

			const isLoggedUser = data?.delete_user_properties?.returning[0]?.user_id === loggedUserValue.id;

			if (refreshToken && isLoggedUser) {
				await refreshToken();
			}
		},
		[loggedUserValue.id, refreshToken]
	);

	const handleOnErrorDeleteUserProperties = useCallback((error: ApolloError) => {
		console.log("Delete User Properties Error: ", error);
	}, []);

	const [deleteUserPropertiesMutation, { loading: deleteUserPropertiesLoading }] = useDeleteUserPropertiesMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorDeleteUserProperties,
		onCompleted: handleOnCompleteDeleteUserProperties
	});

	const handleOnCompleteDeletePropertyDocument = useCallback((data: any) => {
		// console.log("Complete Delete Property Document data: ", data);
	}, []);

	const handleOnErrorDeletePropertyDocument = useCallback((error: ApolloError) => {
		//  console.log("Delete Property Document Error: ", error);
	}, []);

	const [deletePropertyDocumentByIdMutation, { loading: deletePropertyDocumentLoading }] =
		useDeletePropertyDocumentByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorDeletePropertyDocument,
			onCompleted: handleOnCompleteDeletePropertyDocument
		});

	const handleOnCompleteAddPropertyDocuments = useCallback((data: any) => {
		// console.log("Complete Add Property Documents data: ", data);
	}, []);

	const handleOnErrorAddPropertyDocuments = useCallback((error: ApolloError) => {
		console.log("Add Property Documents  Error: ", error);
	}, []);

	const [addPropertyDocumentsMutation, { loading: addUserPropertyDocumentsLoading }] = useAddPropertyDocumentsMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddPropertyDocuments,
		onCompleted: handleOnCompleteAddPropertyDocuments
	});

	const [extractPropertyFloorPlanMutation] = useExtractPropertyFloorPlanMutation({
		onError: error => console.log("Extract Property Floor Plan Error: ", error)
	});

	const handleOnRemovePhoto = useCallback(async () => {
		const { photo_link = "" } = propertyData || {};

		// Extract and store the storage path from the URL
		if (photo_link) {
			// Firebase Storage URLs contain a token, we need to extract just the path

			try {
				await deleteFromStorage(photo_link);
			} catch (error) {
				console.error("Error deleting Property Photo from firebase:", error);
			}

			try {
				// Update property in database to remove the image URL
				await updatePropertyByIdMutation({
					variables: {
						set: {
							photo_link: null
						},
						id: params.propertyId
					}
				});

				// Clear local state
				setPropertyPhotoThumbUrl("");
				setPropertyPhotoFile(null);

				message.success("Property photo deleted successfully");
			} catch (error) {
				console.error("Error deleting Property photo:", error);
				message.error("Failed to delete Property photo");
			}
		}
	}, [propertyData, params.propertyId, updatePropertyByIdMutation]);

	const uploadPropertyImg = useCallback(async () => {
		if (propertyPhotoFile) {
			try {
				const uniqueID = uuidv4();

				const bucketName = "property-photos/";

				const fileName = `property-photo-${name}-${uniqueID}-${Math.floor(
					Date.now() / 1000
				)}-${propertyPhotoFile.name}`;

				const { publicUrl } = await uploadToStorage(propertyPhotoFile, () => {}, `${bucketName}/${fileName}`);

				return publicUrl;
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
				return "";
			}
		}
	}, [propertyPhotoFile, name]);

	const handleUpdatePropertyContact = useCallback(async () => {
		const { property_contacts = [] } = propertyData || {};

		const contact = {
			name: contactName || null,
			number: contactPhoneNumber || null,
			email: contactEmail || null
		};

		if (property_contacts.length > 0) {
			try {
				const propertyContactId = property_contacts[0].id;

				await updatePropertyContactByIdMutation({
					variables: {
						set: contact,
						id: propertyContactId
					}
				});
			} catch (error) {
				console.error("Error updating Property Contact: ", error);
				message.error("Failed to update Property Contact.");
			}
		} else if (contact.name || contact.number || contact.email) {
			try {
				const newContact = {
					...contact,
					property_id: params.propertyId
				};

				await addPropertyContactMutation({
					variables: {
						input: newContact
					}
				});
			} catch (error) {
				console.error("Error adding Property Contact: ", error);
				message.error("Failed to add Property Contact.");
			}
		}
	}, [
		propertyData,
		updatePropertyContactByIdMutation,
		addPropertyContactMutation,
		params.propertyId,
		contactName,
		contactEmail,
		contactPhoneNumber
	]);

	const uploadDocuments = useCallback(async () => {
		const drawingsWithType = drawingsFiles.map(f => ({ ...f, type: f.type || PropertyDocumentsType.DRAWING }));
		const floorPlansWithType = floorPlanFiles.map(f => ({ ...f, type: PropertyDocumentsType.FLOOR_PLAN }));
		let propertyDocuments;

		if (documentsFiles.length > 0) {
			if (warrantyDocFile) {
				propertyDocuments = [warrantyDocFile, ...documentsFiles, ...drawingsWithType];
			} else {
				propertyDocuments = [...documentsFiles, ...drawingsWithType];
			}
		} else {
			if (warrantyDocFile) {
				propertyDocuments = [warrantyDocFile, ...drawingsWithType];
			} else {
				propertyDocuments = [...drawingsWithType];
			}
		}

		propertyDocuments = [...propertyDocuments, ...floorPlansWithType];

		if (propertyDocuments.length > 0) {
			try {
				const promises: Promise<Property_Documents_Insert_Input | undefined>[] = [];

				propertyDocuments.forEach((document, index) => {
					const { type, description, file, name } = document;

					const fileMediaType = file.type.split("/")[0];
					const uniqueID = uuidv4();

					let bucketName = "property-documents/";
					let attachmentName = "";

					const fileExtension = getFileExtension(file.name);
					let fileName = name ? `${name}.${fileExtension}` : file.name;

					const newDocument: Property_Documents_Insert_Input = {
						photo_link: null,
						document_link: null,
						video_link: null,
						audio_link: null,
						type,
						description,
						name: fileName,
						property_id: params.propertyId
					};

					switch (fileMediaType) {
						case "image":
							bucketName = bucketName + "photos";
							attachmentName = `property-document-photo-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "video":
							bucketName = bucketName + "videos";
							attachmentName = `property-document-video-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "audio":
							bucketName = bucketName + "audios";
							attachmentName = `property-document-audio-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						default:
							bucketName = bucketName + "documents";
							attachmentName = `property-document-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
					}

					const uploadTask = async () => {
						try {
							const fileObj = file?.originFileObj;

							const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`);

							switch (fileMediaType) {
								case "image":
									newDocument.photo_link = publicUrl;
									break;
								case "video":
									newDocument.video_link = publicUrl;
									break;
								case "audio":
									newDocument.audio_link = publicUrl;
									break;
								default:
									newDocument.document_link = publicUrl;
									break;
							}

							return newDocument;
						} catch (error) {
							console.log("Error when trying to upload the document file to firestore. Error: ", error);
						}
					};

					promises.push(uploadTask());
				});

				try {
					const promisesResults = await Promise.all(promises);

					console.log("promisesResults: ", promisesResults);

					const filteredPromisesResults = pruneEmptyValues(promisesResults).filter(
						val => val
					) as Property_Documents_Insert_Input[];

					await addPropertyDocumentsMutation({
						variables: {
							objects: filteredPromisesResults
						}
					});

					// New floor plans were uploaded — bootstrap the room hierarchy with
					// Ollie, fired here (after the docs mutation resolves) so all plans are
					// present. replace=false + the backend idempotency guard means it
					// never clobbers an existing hierarchy. Fire-and-forget.
					if (floorPlanFiles.length > 0 && params.propertyId) {
						extractPropertyFloorPlanMutation({
							variables: { input: { property_id: params.propertyId, replace: false } }
						});
					}
				} catch (error) {
					console.log("Error trying to upload documents: ", error);
				}
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			}
		}

		return;
	}, [
		documentsFiles,
		drawingsFiles,
		floorPlanFiles,
		warrantyDocFile,
		params.propertyId,
		addPropertyDocumentsMutation,
		extractPropertyFloorPlanMutation
	]);

	const handleUpdateUsers = useCallback(async () => {
		const { property_users = [] } = propertyData || {};

		if (property_users.length) {
			const currentPropertyUsers: Array<Users> = property_users.reduce((result: Array<Users>, element) => {
				if (element.user) {
					result.push(element.user as Users);
				}
				return result;
			}, []);

			const comparison = compareEntities(currentPropertyUsers, selectedUsers);

			if (comparison.toRemove.length) {
				try {
					const usersIdsToRemove = comparison.toRemove.map(user => user.id);

					await deleteUserPropertiesMutation({
						variables: {
							where: {
								property_id: { _eq: params.propertyId },
								user_id: { _in: usersIdsToRemove }
							}
						}
					});
				} catch (error) {
					console.log("Error on delete users properties. Error: ", error);
				}
			}

			if (comparison.toAdd.length) {
				try {
					const usersToAdd = comparison.toAdd.map(user => {
						return {
							property_id: params.propertyId,
							user_id: user.id
						};
					});

					await addUserPropertiesMutation({
						variables: {
							objects: usersToAdd
						}
					});
				} catch (error) {
					console.log("Error on add users properties. Error: ", error);
				}
			}
		} else {
			if (selectedUsers.length) {
				try {
					const usersToAdd = selectedUsers.map(user => {
						return {
							property_id: params.propertyId,
							user_id: user.id
						};
					});

					await addUserPropertiesMutation({
						variables: {
							objects: usersToAdd
						}
					});
				} catch (error) {
					console.log("Error on add users properties. Error: ", error);
				}
			}
		}
	}, [params.propertyId, selectedUsers, propertyData, addUserPropertiesMutation, deleteUserPropertiesMutation]);

	const handleMyQSetup = async () => {
		setMyqSetupLoading(true);
		message.loading({
			content: "Initiating myQ Webhook Setup...",
			key: "myqSetup"
		});
		try {
			const returnURI = encodeURIComponent(window.location.href);

			const authURL = `${process.env.REACT_APP_MYQ_ACCOUNT_URL}?partnerId=${process.env.REACT_APP_MYQ_PARTNER_ID}&ActionIdentifier=${params.propertyId}&ReturnURI=${returnURI}`;

			window.location.href = authURL;

			// TODO: Add Webhook config, not doing this yet bc we only have access to 1 partner with myQ, so there is nothing unique to configure here yet
			// const getUrl = 'http://localhost/myq/configure_webhook';
			// const getConfig = {
			//   method: 'GET',
			//   headers: {
			//     'Content-Type': 'application/json'
			//   }
			// }
			// const response = await fetch(getUrl, getConfig);
			// const result = await response.json();
			// console.log(result);

			// if (response.ok) {
			//   message.success({ content: result.message || 'myQ Setup Initiated Successfully!', key: 'myqSetup', duration: 5 });
			//   // Optionally refresh data or update UI state here
			// } else {
			//   // Use the error message from the backend response if available
			//   throw new Error(result.message || `Server responded with status ${response.status}`);
			// }
		} catch (error: any) {
			console.error("myQ Setup failed:", error);
			message.error({
				content: `myQ Setup Failed: ${error.message}`,
				key: "myqSetup",
				duration: 5
			});
		} finally {
			setMyqSetupLoading(false);
		}
	};

	const handleOnSubmit = useCallback(async () => {
		if (params.propertyId) {
			try {
				setUploading(true);
				try {
					let addressIdToSet: string | null | undefined = undefined;
					if (selectedAddressComponents) {
						const addressPayload = {
							street_1: selectedAddressComponents.street_1,
							street_2: selectedAddressComponents.street_2 || null,
							city: selectedAddressComponents.city,
							state: selectedAddressComponents.state,
							postal_code: selectedAddressComponents.postal_code,
							country: "US"
						};
						let foundAddressId = null;
						try {
							const { data } = await getAddressByDetails({
								variables: addressPayload,
								fetchPolicy: "network-only"
							});
							if (data?.addresses && data.addresses.length > 0) {
								foundAddressId = data.addresses[0].id;
							}
						} catch (error) {
							console.error("Error checking address existence", error);
						}

						if (foundAddressId) {
							addressIdToSet = foundAddressId;
							setExistingAddressId(foundAddressId);
						} else if (existingAddressId) {
							await updateAddressByIdMutation({
								variables: { id: existingAddressId, set: addressPayload }
							});
						} else {
							try {
								const result = await insertAddressMutation({
									variables: { input: addressPayload }
								});
								const newAddressId = result.data?.insert_addresses_one?.id;
								if (newAddressId) {
									addressIdToSet = newAddressId;
									setExistingAddressId(newAddressId);
								}
							} catch (addressError) {
								console.log("Error inserting address: ", addressError);
								message.error("Failed to save address. The address may already exist.");
								setUploading(false);
								return;
							}
						}
						setSelectedAddressComponents(null);
					} else {
						// If the user modified the address text without picking an autocomplete option,
						// disassociate it from the old structured address
						if (
							existingAddressId &&
							address !== getPropertyAddress(propertyData?.address, propertyData?.standardized_address)
						) {
							addressIdToSet = null;
							setExistingAddressId(null);
						}
					}

					let propertyInput: Properties_Set_Input = {
						name: name,
						address: address,
						premium: isPremium,
						...(addressIdToSet !== undefined ? { address_id: addressIdToSet } : {})
					};

					let photoLink;
					if (propertyPhotoFile) {
						photoLink = await uploadPropertyImg();
						propertyInput.photo_link = photoLink;
					}

					if (warrantyDateStart) {
						propertyInput.warranty_start_date = warrantyDateStart.format("YYYY-MM-DDTHH:mm:ssZ");
					}

					if (warrantyDateEnd) {
						propertyInput.warranty_expiration_date = warrantyDateEnd.format("YYYY-MM-DDTHH:mm:ssZ");
					}

					if (buildingType) {
						propertyInput.building_type = buildingType;
					}

					if (occupancyClass) {
						propertyInput.occupancy_class = occupancyClass;
					}

					if (numberOfStories) {
						propertyInput.num_stories = parseInt(numberOfStories, 10);
					}

					if (numberOfUnits) {
						propertyInput.num_units = parseInt(numberOfUnits, 10);
					}

					if (constructionType) {
						propertyInput.construction_type = constructionType;
					}

					if (fireSuppression) {
						propertyInput.sprinklered = fireSuppression;
					}

					if (yearBuilt) {
						propertyInput.year_built = parseInt(yearBuilt, 10);
					}

					if (expectedDeviceTypes && expectedDeviceTypes.length > 0) {
						propertyInput.expected_device_type = expectedDeviceTypes;
					}

					await updatePropertyByIdMutation({
						variables: {
							set: propertyInput,
							id: params.propertyId
						}
					});
				} catch (error) {
					console.log("Error on submit property update. Error: ", error);
				}

				try {
					await handleUpdatePropertyContact();
				} catch (error) {
					console.log("Error on update property contact. Error: ", error);
				}

				if (documentsFiles.length || drawingsFiles.length || floorPlanFiles.length || warrantyDocFile) {
					try {
						await uploadDocuments();
					} catch (error) {
						console.log("Error on upload Documents. Error: ", error);
					}
				}

				try {
					await handleUpdateUsers();
				} catch (error) {
					console.log("Error on submit users properties update. Error: ", error);
				}
			} catch (error) {
				console.log("Error on submit property update. Error: ", error);
			} finally {
				setUploading(false);
				const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(":propertyId", params.propertyId)}`;

				navigate(path);
			}
		}
	}, [
		propertyData,
		params.propertyId,
		name,
		address,
		isPremium,
		warrantyDateStart,
		warrantyDateEnd,
		warrantyDocFile,
		documentsFiles,
		drawingsFiles,
		buildingType,
		occupancyClass,
		numberOfStories,
		numberOfUnits,
		constructionType,
		fireSuppression,
		yearBuilt,
		expectedDeviceTypes,
		selectedAddressComponents,
		existingAddressId,
		propertyPhotoFile,
		getAddressByDetails,
		updateAddressByIdMutation,
		insertAddressMutation,
		updatePropertyByIdMutation,
		uploadPropertyImg,
		handleUpdatePropertyContact,
		uploadDocuments,
		handleUpdateUsers
	]);

	const isLoading =
		uploading ||
		loadingPropertyById ||
		loadingUsersByOrganization ||
		updatePropertyByIdLoading ||
		deleteUserPropertiesLoading ||
		addUserPropertiesLoading ||
		addPropertyContactLoading ||
		updatePropertyContactByIdLoading ||
		deletePropertyDocumentLoading ||
		addUserPropertyDocumentsLoading ||
		myqSetupLoading;

	const usersOptions = useMemo(() => {
		const { users } = usersByOrganizationData || {};

		if (users && users.length) {
			const usersOptionsItems: MenuProps["items"] = users.map((user, currentindex, array) => {
				const optionId = `user-dropdown-option-${currentindex + 1}-${user?.id}`;

				const isSelected = selectedUsersKeys.includes(user?.id as string);

				const newOption = {
					key: `${user?.id}`,
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id={optionId}
								type="checkbox"
								onClick={e => e.stopPropagation()}
								checked={isSelected}
								onChange={() => undefined}
							/>
							<label htmlFor={optionId} className="custom-checkbox-label">
								{`${user?.first_name} ${user?.last_name}`}
							</label>
						</div>
					)
				};

				return newOption;
			});

			return usersOptionsItems;
		}

		return [];
	}, [usersByOrganizationData, selectedUsersKeys]);

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"Edit Property"}</span>
			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"Property Details"}</span>
				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the property name." }]}
					required
					validateTrigger="onBlur"
				>
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Name"}</span>
						<Input
							name="name"
							placeholder="Property Name"
							className="add-product-input-pair"
							value={name}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setName(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>

				<Form.Item
					className="login-form-item"
					rules={[
						{
							required: true,
							message: "Please input the property address."
						}
					]}
					required
					validateTrigger="onBlur"
				>
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Address"}</span>
						<AutoComplete
							options={addressOptions}
							onSearch={handleAddressSearch}
							onSelect={handleAddressSelect}
							onChange={(value: string) => {
								setAddress(value);
								if (!value) {
									setAddressOptions([]);
									setSelectedAddressComponents(null);
								}
							}}
							value={address}
							className="add-product-input-pair"
							disabled={isLoading}
							allowClear
						>
							<Input
								name="address"
								placeholder="Property Address"
								autoComplete="off"
								className="add-product-input-pair"
							/>
						</AutoComplete>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Construction Type (optional)"}</span>
						<Select
							placeholder="Select a Construction Type"
							className="add-product-input-pair"
							value={constructionType || undefined}
							onChange={value => setConstructionType(value)}
							options={CONSTRUCTION_TYPE_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Type (optional)"}</span>
						<Select
							placeholder="Select a Property Type"
							className="add-product-input-pair"
							value={buildingType || undefined}
							onChange={value => setBuildingType(value)}
							options={BUILDING_TYPE_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Occupancy Class (optional)"}</span>
						<Select
							placeholder="Select an Occupancy Class"
							className="add-product-input-pair"
							value={occupancyClass || undefined}
							onChange={value => setOccupancyClass(value)}
							options={OCCUPANCY_CLASS_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Number of Stories (optional)"}</span>
						<Select
							placeholder="Number of Stories"
							className="add-product-input-pair"
							value={numberOfStories || undefined}
							onChange={value => setNumberOfStories(value)}
							options={NUMBER_OF_STORIES_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Number of Units (optional)"}</span>
						<Select
							placeholder="Number of Units"
							className="add-product-input-pair"
							value={numberOfUnits || undefined}
							onChange={value => setNumberOfUnits(value)}
							options={NUMBER_OF_UNITS_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Fire Suppression System (optional)"}</span>
						<Select
							placeholder="Select System Type"
							className="add-product-input-pair"
							value={fireSuppression || undefined}
							onChange={value => setFireSuppression(value)}
							options={FIRE_SUPPRESSION_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Year Built (optional)"}</span>
						<Select
							placeholder="Select Year"
							className="add-product-input-pair"
							value={yearBuilt || undefined}
							onChange={value => setYearBuilt(value)}
							options={YEAR_BUILT_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Device Types (optional)"}</span>
						<span className="property-form-field-description">
							{
								"These are used when analyzing building documentation to identify relevant devices. Press 'Enter' after each device type."
							}
						</span>
						<Select
							mode="tags"
							placeholder="Enter Expected Device Types"
							className="add-product-input-pair"
							value={expectedDeviceTypes}
							onChange={(values: string[]) => {
								setExpectedDeviceTypes(values);
							}}
							allowClear
							disabled={isLoading}
							tokenSeparators={[","]}
						/>
					</div>
				</Form.Item>
				{/* See MWA-238 */}
				{/* {loggedUserValue.role === UserRoleValue.customer_manager ? null : (
          <div className="organization-button-container">
            <span className="switch-label">Premium</span>
            <Switch
              checked={isPremium}
              onChange={(checked) => setIsPremium(checked)}
              disabled={isLoading}
            />
          </div>
        )} */}

				<div className="organization-button-container">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Image"}</span>
						<Button
							className="upload-product-button"
							onClick={() => setAddPropertyPhotoModalOpen(true)}
							disabled={isLoading}
						>
							Upload Property Image
						</Button>
					</div>
				</div>

				{propertyPhotoThumbUrl ? (
					<div className="organization-button-container">
						<Image width={170} src={propertyPhotoThumbUrl} />{" "}
						<img
							alt={`remove-organization-photo-icon`}
							src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
							className="organization-photo-action-icon"
							onClick={() => {
								if (!isLoading) {
									handleOnRemovePhoto();
								}
							}}
						/>
					</div>
				) : null}

				<UploadPhotoModal
					open={addPropertyPhotoModalOpen}
					onCancel={() => setAddPropertyPhotoModalOpen(false)}
					uploadMethod={handleOnConfirmPhoto}
					disableCrop
				/>
			</Form>

			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"Primary Contact"}</span>
				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Name (optional)"}</span>
						<span className="property-form-field-description">
							{"First and last name of the primary point of contact for this property."}
						</span>
						<Input
							name="contactName"
							placeholder="Primary Contact Name"
							className="add-product-input-pair"
							value={contactName}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setContactName(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Phone Number (optional)"}</span>
						<Input
							name="contactPhoneNumber"
							placeholder="Phone Number"
							className="add-product-input-pair"
							value={contactPhoneNumber}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setContactPhoneNumber(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Email (optional)"}</span>
						<Input
							name="contactEmail"
							placeholder="Email Address"
							className="add-product-input-pair"
							value={contactEmail}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setContactEmail(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>
			</Form>

			<div className="add-product-form">
				<span className="add-product-form-title">{"Plans & Documentation"}</span>
				<p className="property-form-field-description">
					{"Note: plans that are part of a submittal package will be uploaded separately during Plan Review."}
				</p>

				{/* Drawings — hidden, see SHOW_DRAWINGS_SECTION */}
				{SHOW_DRAWINGS_SECTION && (
					<>
						<span className="property-form-subtitle">{"Drawings (optional)"}</span>
						<p className="property-form-field-description">
							{"Upload relevant design, construction, or as-built drawings."}
						</p>
						<div className="add-product-documents">
							<Button className="upload-product-button" onClick={() => setDrawingsModalOpen(true)} disabled={isLoading}>
								Add Drawings
							</Button>
						</div>

						{existingDrawingsFiles && existingDrawingsFiles.length > 0 ? (
							<div className="attachments-container">
								{existingDrawingsFiles.map(documentFile => {
									const uid = documentFile.uid || documentFile.id;

									return (
										<AttachmentItem
											key={`property-drawing-item-${uid}`}
											name={documentFile.name}
											file={documentFile.file}
											fileUrl={
												documentFile.photo_link ||
												documentFile.document_link ||
												documentFile.video_link ||
												documentFile.audio_link ||
												documentFile.site_link
											}
											type={documentFile.type}
											onRemove={() => {
												if (!isLoading) {
													handleOnRemoveExistingAttachment(documentFile);
												}
											}}
											showEditButton={false}
										/>
									);
								})}
							</div>
						) : null}

						{drawingsFiles && drawingsFiles.length > 0 ? (
							<div className="attachments-container">
								{drawingsFiles.map(documentFile => {
									const uid = documentFile.uid || documentFile.id;

									return (
										<AttachmentItem
											key={`property-drawing-item-${uid}`}
											name={documentFile.name}
											file={documentFile.file}
											type={documentFile.type}
											onRemove={() => {
												if (!isLoading) {
													handleOnRemoveDrawing(documentFile);
												}
											}}
											showEditButton={false}
										/>
									);
								})}
							</div>
						) : null}
					</>
				)}

				{/* Plans — feed the Ollie room extraction */}
				<span className="property-form-subtitle">{"Plans (optional)"}</span>
				<p className="property-form-field-description">
					{
						"Upload property plans (PDF or image). Our AI will extract a building/floor/room structure for tagging evidence during inspection."
					}
				</p>
				<div className="add-product-documents">
					<Button className="upload-product-button" onClick={() => setFloorPlansModalOpen(true)} disabled={isLoading}>
						Add Plans
					</Button>
				</div>

				{existingFloorPlanFiles && existingFloorPlanFiles.length > 0 ? (
					<div className="attachments-container">
						{existingFloorPlanFiles.map(documentFile => {
							const uid = documentFile.uid || documentFile.id;
							return (
								<AttachmentItem
									key={`property-floor-plan-item-${uid}`}
									name={documentFile.name}
									file={documentFile.file}
									fileUrl={documentFile.document_link || documentFile.photo_link}
									type={documentFile.type}
									onRemove={() => {
										if (!isLoading) handleOnRemoveExistingAttachment(documentFile);
									}}
									showEditButton={false}
								/>
							);
						})}
					</div>
				) : null}

				{floorPlanFiles && floorPlanFiles.length > 0 ? (
					<div className="attachments-container">
						{floorPlanFiles.map(documentFile => {
							const uid = documentFile.uid || documentFile.id;
							return (
								<AttachmentItem
									key={`property-floor-plan-new-item-${uid}`}
									name={documentFile.name}
									file={documentFile.file}
									type={documentFile.type}
									onRemove={() => {
										if (!isLoading) handleOnRemoveFloorPlan(documentFile);
									}}
									showEditButton={false}
								/>
							);
						})}
					</div>
				) : null}

				<span className="property-form-subtitle">{"Property Resources (optional)"}</span>
				<p className="property-form-field-description">
					{
						"Upload other reference documentation for this property, such as close-out packages, as-builts, and walk-through videos."
					}
				</p>
				<div className="add-product-documents">
					<Button className="upload-product-button" onClick={() => setAddDocumentModalOpen(true)} disabled={isLoading}>
						Add Resources
					</Button>
				</div>

				{existingDocumentsFiles && existingDocumentsFiles.length > 0 ? (
					<div className="attachments-container">
						{existingDocumentsFiles.map(documentFile => {
							const uid = documentFile.uid || documentFile.id;

							return (
								<AttachmentItem
									key={`property-document-item-${uid}`}
									name={documentFile.name}
									file={documentFile.file}
									fileUrl={
										documentFile.photo_link ||
										documentFile.document_link ||
										documentFile.video_link ||
										documentFile.audio_link ||
										documentFile.site_link
									}
									type={documentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveExistingAttachment(documentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditDocument(documentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}

				{documentsFiles && documentsFiles.length > 0 ? (
					<div className="attachments-container">
						{documentsFiles.map(documentFile => {
							const uid = documentFile.uid || documentFile.id;

							return (
								<AttachmentItem
									key={`property-document-item-${uid}`}
									name={documentFile.name}
									file={documentFile.file}
									type={documentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveDocument(documentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditDocument(documentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}

				{loggedUserValue.role === UserRoleValue.customer_manager ? null : (
					<>
						<span className="property-form-subtitle">{"Warranty (optional)"}</span>
						<div className="property-add-warranty-container">
							<Button
								className="property-add-warranty-button"
								onClick={() => {
									setWarrantyDocMode(true);
									setAddDocumentModalOpen(true);
								}}
								disabled={isLoading}
							>
								Upload Warranty Document
							</Button>

							<DatePicker
								allowClear
								className="property-add-warranty-date"
								format={"MM/DD/YYYY"}
								placeholder={"Select Start Date"}
								value={warrantyDateStart}
								onChange={(date, dateString) => {
									if (date) {
										setWarrantyDateStart(date);
									} else {
										setWarrantyDateStart(null);
									}
								}}
								disabled={isLoading}
							/>

							<DatePicker
								allowClear
								className="property-add-warranty-date"
								format={"MM/DD/YYYY"}
								placeholder={"Select Expiration Date"}
								value={warrantyDateEnd}
								onChange={(date, dateString) => {
									if (date) {
										setWarrantyDateEnd(date);
									} else {
										setWarrantyDateEnd(null);
									}
								}}
								disabled={isLoading}
							/>
						</div>

						{existingWarrantyDocFile ? (
							<div className="attachments-container ">
								<AttachmentItem
									key={`property-document-item-${existingWarrantyDocFile.uid || existingWarrantyDocFile.id}`}
									name={existingWarrantyDocFile.name}
									file={existingWarrantyDocFile.file}
									type={existingWarrantyDocFile.type}
									fileUrl={
										existingWarrantyDocFile.photo_link ||
										existingWarrantyDocFile.document_link ||
										existingWarrantyDocFile.video_link ||
										existingWarrantyDocFile.audio_link ||
										existingWarrantyDocFile.site_link
									}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveExistingAttachment(existingWarrantyDocFile);
										}
									}}
									showEditButton={false}
								/>
							</div>
						) : null}

						{warrantyDocFile ? (
							<div className="attachments-container ">
								<AttachmentItem
									key={`property-document-item-${warrantyDocFile.uid || warrantyDocFile.id}`}
									name={warrantyDocFile.name}
									file={warrantyDocFile.file}
									type={warrantyDocFile.type}
									onRemove={() => {
										if (!isLoading) {
											setWarrantyDocFile(null);
										}
									}}
								/>
							</div>
						) : null}
					</>
				)}
			</div>

			<div className="add-product-form">
				<span className="add-product-form-title">{"Access"}</span>

				<div className="property-form-field-with-description">
					<span className="property-form-field-label">{"Users"}</span>
					<span className="property-form-field-description">
						{"Who should have access to view this property? Additional permissions are configured separately."}
					</span>
				</div>

				<Dropdown
					menu={{
						items: usersOptions,
						selectable: true,
						multiple: true,
						onSelect: args => handleOnSelectUsers(args),
						onDeselect: args => handleOnSelectUsers(args),
						selectedKeys: selectedUsersKeys
					}}
					arrow
					className="organization-properties-dropdown"
					overlayClassName="products-dropdown-overlay"
					trigger={["click"]}
					open={usersDropdownOpen}
					onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
						if (info.source === "trigger" && open === false) {
							setUsersDropdownOpen(false);
						}
					}}
				>
					<Button
						onClick={() => {
							setUsersDropdownOpen(previousValue => !previousValue);
						}}
						disabled={isLoading}
					>
						Select Users
						<img
							src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
							className="chevron-button"
							title={"Select Users"}
						/>
					</Button>
				</Dropdown>
				<div className="organization-selected-properties-container">
					{selectedUsers.map((user: Users) => {
						return (
							<div key={user.id} className="organization-selected-property">
								<span className="organization-selected-property-name">{`${user?.first_name} ${user?.last_name}`}</span>
								<span
									className="organization-selected-property-delete"
									onClick={() => {
										if (!isLoading) {
											handleOnRemoveUser(user.id);
										}
									}}
								>
									{"X"}
								</span>
							</div>
						);
					})}
				</div>
			</div>

			{/* --- MyQ Integration Section --- */}
			<div
				className="add-product-form"
				style={{
					marginTop: "30px",
					borderTop: "1px solid #f0f0f0",
					paddingTop: "20px"
				}}
			>
				<span className="add-product-form-title">{"Integrations"}</span>
				<div className="login-form-item">
					<span className="switch-label" style={{ marginRight: "20px" }}>
						myQ Smart Devices Control
					</span>
					<Button
						type="primary"
						onClick={handleMyQSetup}
						loading={myqSetupLoading}
						disabled={isLoading} // Disable if any other operation is in progress
					>
						Configure myQ Webhook
					</Button>
					<p style={{ marginTop: "10px", color: "#888", fontSize: "12px" }}>
						Click to configure the webhook required to receive real-time device events from your myQ account.
					</p>
				</div>
			</div>
			{/* --- End MyQ Integration Section --- */}

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit} disabled={isLoading}>
					Save
				</Button>
			</div>

			<AddAttachmentModal
				open={drawingsModalOpen}
				onCancel={() => setDrawingsModalOpen(false)}
				updateAttachments={updateDrawings}
				editMode={false}
				editProduct={true}
				propertyDocMode={true}
				warrantyDocMode={false}
				fixedCategory={PropertyDocumentsType.DRAWING}
			/>

			<AddAttachmentModal
				open={floorPlansModalOpen}
				onCancel={() => setFloorPlansModalOpen(false)}
				updateAttachments={updateFloorPlans}
				editMode={false}
				editProduct={true}
				propertyDocMode={true}
				warrantyDocMode={false}
				fixedCategory={PropertyDocumentsType.FLOOR_PLAN}
			/>

			<AddAttachmentModal
				open={addDocumentModalOpen}
				onCancel={handleOnCloseDocumentModal}
				updateAttachments={updateAttachments}
				editMode={addDocumentModalEditMode}
				editProduct={true}
				propertyDocMode={true}
				warrantyDocMode={warrantyDocMode}
			/>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default PropertyEdit;
